import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import { FilterToggleButton } from '~/pages/SpotBidding/components/FilterToggleButton'
import { FILTER_RAIL_WIDTH } from '~/pages/SpotBidding/constants/filterSidebar'

type Props = {
  /** レールのトグルを押した時 (= 展開)。 開いている時と同じ handler を渡す。 */
  onExpand: () => void
  /** 何らかの絞り込みが効いているか (レールに印を出す)。 */
  hasActiveFilter: boolean
}

/**
 * フィルターサイドバーを畳んだ時に残す 48px のレール。
 *
 * ## なぜ「完全に隠す」 のをやめたか
 *
 * #7449 の初版は閉じている間**要素ごと描かなかった**。 デザイナー指摘
 * (2026-09-15)「フィルターを完全に隠すのではなく、 side menu のように補足表示して
 * 開いたり畳めたりできるようにしたい」 を受けて、 列を落とさずレールを残す。
 *
 * 完全に隠すと、 閉じている間は**絞り込みが効いていること自体が画面から消える**。
 * 一覧の件数だけが減って見えるので、 「データが無い」 のか「絞り込んでいる」 のかが
 * 区別できない。 レールに `絞り込み中` の印を出すことでそこを埋める。
 *
 * ## 8080 の side menu に倣った点
 *
 * - **幅 48px** (`.sidebar.collapsed`)。 ラベルは畳むが**レール自体は残す**
 * - アイコンは「今の状態」 ではなく**押すと何が起きるか**を描く
 *   (`include-nav.js`: `collapsed ? EXPAND_ICON : COLLAPSE_ICON`)
 *
 * ## 押せるのは上端 56px だけ
 *
 * 展開の導線は `FilterToggleButton` で、 開いている時の折りたたみ導線と**同じ
 * 部品・同じ座標**にする (2026-09-15 デザイナー指摘「折りたたむ導線は、 広げる
 * 導線と同じ場所に配置して、 開閉を連打できるようにしたい」)。 レール全体を
 * `<button>` にするとトグルを入れ子にできない (ボタンの入れ子は不正) ため、
 * 下のラベルと印は非対話の表示に留める。
 *
 * ## 枠線をレールが持つ理由
 *
 * 初版が「要素ごと描かない」 を選んだのは、 width 0 で残すと隣の一覧カードと共有
 * している 1px の辺が**筋として残る**ためだった。 レールは実体のある列なので、
 * 元のサイドバーと同じ辺の持ち方 (左・上・下を持ち、 右は隣のカードへ集約) を
 * そのまま引き継げばよく、 筋の問題は起きない。
 */
export const FilterRail = ({
  onExpand,
  hasActiveFilter,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Flex
      alignItems="center"
      flexDirection="column"
      width={FILTER_RAIL_WIDTH}
      height="100%"
    >
      <FilterToggleButton isOpen={false} onToggle={onExpand} />
      <Flex alignItems="center" flexDirection="column" gap={2}>
        {/* 縦書きでラベルを残す。 アイコンだけだと何のレールか分からず、
            side menu と違ってフィルターには確立したアイコンの語彙が無い。

            `writing-mode` は本アプリ初出 (他に使用箇所なし)。 48px に横書きの
            「フィルター」 は入らず、 省略すると何のレールか読めなくなるため採った。 */}
        <Text
          textStyle="captionNormal"
          color="Text.midEmphasis"
          css={{ writingMode: 'vertical-rl' }}
        >
          {t('page:biddingSpot.sidebar.filterTitle')}
        </Text>
        {/* 絞り込みが効いている時だけ印を出す。 畳んでいる間に「今フィルターが
            効いている」 ことが分からないと、 件数が少ないのを空データと読み違える。 */}
        {hasActiveFilter && (
          <Box
            width="6px"
            height="6px"
            borderRadius="full"
            aria-label={t('page:biddingSpot.sidebar.filterRailActive')}
            backgroundColor="Surface.accentPrimary"
            role="img"
          />
        )}
      </Flex>
    </Flex>
  )
}
